import React, { useEffect, useState } from 'react';
import DashboardLayout from '../../components/Layout/DashboardLayout';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import * as creditService from '../../services/creditService';
import { toast } from 'react-toastify';

export default function Referrals() {
	const [loading, setLoading] = useState(true);
	const [stats, setStats] = useState({ code: '', total: 0, credits: 0, users: [] });

	useEffect(() => {
		let mounted = true;
		(async () => {
			try {
				const res = await creditService.getReferralStats();
				if (mounted) {
					setStats({
						code: res.referralCode || '',
						total: res.totalReferrals || 0,
						credits: res.totalReferralCredits || 0,
						users: res.referredUsers || []
					});
				}
			} catch {
			} finally {
				if (mounted) setLoading(false);
			}
		})();
		return () => {
			mounted = false;
		};
	}, []);

	function copy(text) {
		navigator.clipboard.writeText(text);
		toast.success('Copied to clipboard');
	}

	if (loading) {
		return (
			<DashboardLayout>
				<LoadingSpinner fullScreen />
			</DashboardLayout>
		);
	}

	const shareLink = `${window.location.origin}/register?ref=${stats.code}`;
	const conversion = stats.total ? Math.round((stats.users.length / stats.total) * 100) : 0;

	return (
		<DashboardLayout>
			<div className="space-y-6">
				<div className="flex items-center justify-between">
					<h1 className="text-2xl font-bold">Invite Friends, Earn Credits</h1>
				</div>

				{/* Stats */}
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
					<StatCard title="Total Referrals" value={stats.total} />
					<StatCard title="Pending Referrals" value={Math.max(0, stats.total - stats.users.length)} />
					<StatCard title="Credits Earned" value={stats.credits} />
					<StatCard title="Conversion Rate" value={`${conversion}%`} />
				</div>

				{/* Code & share */}
				<Card title="Your Referral Code">
					<div className="flex flex-col md:flex-row items-center gap-3">
						<div className="flex items-center gap-2">
							<span className="font-mono bg-gray-50 px-2 py-1 rounded border">{stats.code || '-'}</span>
							<Button size="sm" variant="ghost" onClick={() => copy(stats.code)} disabled={!stats.code}>
								Copy Code
							</Button>
						</div>
						<div className="flex-1 flex items-center gap-2">
							<input value={shareLink} readOnly className="flex-1 rounded-md border border-gray-300 px-3 py-2 bg-gray-50" />
							<Button size="sm" variant="ghost" onClick={() => copy(shareLink)} disabled={!stats.code}>
								Copy Link
							</Button>
						</div>
						<div className="flex items-center gap-2">
							<Button size="sm" variant="secondary" onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(shareLink)}`)}>
								WhatsApp
							</Button>
							<Button size="sm" variant="secondary" onClick={() => window.open(`mailto:?subject=Join Resumly&body=${encodeURIComponent(shareLink)}`)}>
								Email
							</Button>
							<Button size="sm" variant="secondary" onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareLink)}`)}>
								Twitter
							</Button>
						</div>
					</div>
				</Card>

				{/* How it works */}
				<Card title="How It Works">
					<ol className="list-decimal pl-5 text-sm text-gray-700 space-y-2">
						<li>Share your unique referral link or code with friends.</li>
						<li>They sign up and start using Resumly.</li>
						<li>You earn 10 credits for each successful referral.</li>
					</ol>
				</Card>

				{/* History */}
				<Card title="Referral History">
					<div className="overflow-x-auto">
						<table className="min-w-full text-sm">
							<thead className="bg-gray-50">
								<tr>
									<th className="text-left px-4 py-2">User</th>
									<th className="text-left px-4 py-2">Status</th>
									<th className="text-left px-4 py-2">Date</th>
								</tr>
							</thead>
							<tbody>
								{!stats.users.length ? (
									<tr>
										<td colSpan="3" className="px-4 py-6 text-center text-gray-500">
											No referrals yet.
										</td>
									</tr>
								) : (
									stats.users.map((u) => (
										<tr key={u._id} className="border-t">
											<td className="px-4 py-2">{maskEmail(u.email)}</td>
											<td className="px-4 py-2">Completed</td>
											<td className="px-4 py-2">{new Date(u.createdAt).toLocaleDateString()}</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
				</Card>
			</div>
		</DashboardLayout>
	);
}

function StatCard({ title, value }) {
	return (
		<Card>
			<p className="text-sm text-gray-500">{title}</p>
			<p className="mt-1 text-2xl font-bold">{value}</p>
		</Card>
	);
}

function maskEmail(email = '') {
	const [name, domain] = email.split('@');
	if (!domain) return email;
	const hidden = name.slice(0, 2) + '***';
	return `${hidden}@${domain}`;
}


